import { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { P, FF } from "../../styles/tokens";
import { useNavigation } from "../../contexts/NavigationContext";

// Tags y atributos permitidos al renderizar el HTML del markdown legal.
// Cubre todo lo que genera markdownToHtml() abajo. Cualquier <script> o atributo
// peligroso (onclick, src de imagen externo, etc.) queda eliminado por DOMPurify.
const SANITIZE_OPTIONS = {
  ALLOWED_TAGS: [
    "h1", "h2", "h3", "h4", "p", "ul", "ol", "li",
    "strong", "em", "a", "br", "hr", "blockquote",
    "table", "thead", "tbody", "tr", "th", "td", "code",
  ],
  ALLOWED_ATTR: ["href", "title"],
};

/**
 * Renders a markdown legal document (ToS or Privacy Policy)
 * fetched from /public/*.md with basic markdown-to-HTML conversion.
 */
export default function LegalView({ type }) {
  const { setView } = useNavigation();
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);

  const title = type === "terminos" ? "Terminos y Condiciones" : "Aviso de Privacidad";
  const file = type === "terminos" ? "/terminos-y-condiciones.md" : "/aviso-de-privacidad.md";

  useEffect(() => {
    setLoading(true);
    fetch(file)
      .then((r) => r.text())
      .then((md) => {
        setHtml(markdownToHtml(md));
        setLoading(false);
      })
      .catch(() => {
        setHtml("<p>No se pudo cargar el documento.</p>");
        setLoading(false);
      });
  }, [file]);

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 16px 40px" }}>
      {/* Back button */}
      <button
        onClick={() => setView("config")}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "8px 14px", borderRadius: 10, border: `1.5px solid ${P.border}`,
          background: P.bg, color: P.textSm, cursor: "pointer",
          fontFamily: FF, fontSize: 12, fontWeight: 600, marginBottom: 16,
        }}
      >
        ← Volver a Config
      </button>

      {loading ? (
        <p style={{ color: P.textSm, fontFamily: FF, fontSize: 13 }}>Cargando...</p>
      ) : (
        <div
          className="legal-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, SANITIZE_OPTIONS) }}
          style={{
            fontFamily: FF, fontSize: 13, lineHeight: 1.75, color: P.textMd,
            overflowWrap: "break-word",
          }}
        />
      )}

      {/* Inline styles for the rendered markdown */}
      <style>{`
        .legal-content h1 {
          font-size: 20px; font-weight: 800; color: ${P.text};
          margin: 0 0 8px; padding-bottom: 8px;
          border-bottom: 1.5px solid ${P.border};
        }
        .legal-content h2 {
          font-size: 15px; font-weight: 800; color: ${P.text};
          margin: 24px 0 8px;
        }
        .legal-content h3 {
          font-size: 13px; font-weight: 700; color: ${P.text};
          margin: 16px 0 6px;
        }
        .legal-content p {
          margin: 0 0 10px;
        }
        .legal-content strong {
          color: ${P.text}; font-weight: 700;
        }
        .legal-content a {
          color: ${P.ingresos.base}; text-decoration: none; font-weight: 600;
        }
        .legal-content hr {
          border: none; border-top: 1.5px solid ${P.border};
          margin: 20px 0;
        }
        .legal-content table {
          width: 100%; border-collapse: collapse; margin: 12px 0;
          font-size: 12px;
        }
        .legal-content th {
          text-align: left; padding: 8px 10px; font-weight: 700;
          color: ${P.text}; background: ${P.bg};
          border-bottom: 1.5px solid ${P.border};
        }
        .legal-content td {
          padding: 6px 10px; border-bottom: 1px solid ${P.border};
        }
        .legal-content ul, .legal-content ol {
          padding-left: 20px; margin: 6px 0 12px;
        }
        .legal-content li {
          margin-bottom: 4px;
        }
        .legal-content blockquote {
          margin: 12px 0; padding: 10px 16px;
          border-left: 3px solid ${P.ingresos.base};
          background: ${P.bg}; border-radius: 0 8px 8px 0;
        }
        .legal-content em {
          font-style: italic;
        }
      `}</style>
    </div>
  );
}


/**
 * Minimal markdown-to-HTML converter. Handles the subset used
 * in the legal documents: headings, bold, italic, links, tables,
 * lists, blockquotes, horizontal rules, and paragraphs.
 */
function markdownToHtml(md) {
  const lines = md.split("\n");
  const out = [];
  let inTable = false;
  let inList = false;
  let listType = "";

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      if (inList) { out.push(listType === "ul" ? "</ul>" : "</ol>"); inList = false; }
      if (inTable) { out.push("</tbody></table>"); inTable = false; }
      out.push("<hr>");
      continue;
    }

    // Table
    if (line.includes("|") && line.trim().startsWith("|")) {
      const cells = line.split("|").filter((c) => c.trim() !== "");
      // Separator row
      if (cells.every((c) => /^[\s:-]+$/.test(c))) continue;

      if (!inTable) {
        if (inList) { out.push(listType === "ul" ? "</ul>" : "</ol>"); inList = false; }
        out.push("<table><thead><tr>");
        cells.forEach((c) => out.push(`<th>${inline(c.trim())}</th>`));
        out.push("</tr></thead><tbody>");
        inTable = true;
      } else {
        out.push("<tr>");
        cells.forEach((c) => out.push(`<td>${inline(c.trim())}</td>`));
        out.push("</tr>");
      }
      continue;
    }
    if (inTable && !line.includes("|")) {
      out.push("</tbody></table>");
      inTable = false;
    }

    // Headings
    if (line.startsWith("# ")) { closeList(); out.push(`<h1>${inline(line.slice(2))}</h1>`); continue; }
    if (line.startsWith("## ")) { closeList(); out.push(`<h2>${inline(line.slice(3))}</h2>`); continue; }
    if (line.startsWith("### ")) { closeList(); out.push(`<h3>${inline(line.slice(4))}</h3>`); continue; }

    // Blockquote
    if (line.startsWith("> ")) {
      closeList();
      out.push(`<blockquote>${inline(line.slice(2))}</blockquote>`);
      continue;
    }

    // Unordered list
    if (/^[-*] /.test(line.trim())) {
      if (!inList || listType !== "ul") {
        if (inList) out.push("</ol>");
        out.push("<ul>"); inList = true; listType = "ul";
      }
      out.push(`<li>${inline(line.trim().slice(2))}</li>`);
      continue;
    }

    // Ordered list
    if (/^\d+[.)]\s/.test(line.trim())) {
      if (!inList || listType !== "ol") {
        if (inList) out.push("</ul>");
        out.push("<ol>"); inList = true; listType = "ol";
      }
      out.push(`<li>${inline(line.trim().replace(/^\d+[.)]\s/, ""))}</li>`);
      continue;
    }

    // Letter list (a), b), etc.)
    if (/^[a-z]\)\s/.test(line.trim())) {
      if (!inList || listType !== "ol") {
        if (inList) out.push("</ul>");
        out.push('<ol type="a">'); inList = true; listType = "ol";
      }
      out.push(`<li>${inline(line.trim().slice(3))}</li>`);
      continue;
    }

    if (inList && line.trim() === "") { closeList(); continue; }

    // Empty line
    if (line.trim() === "") { closeList(); continue; }

    // Paragraph
    closeList();
    out.push(`<p>${inline(line)}</p>`);
  }

  if (inList) out.push(listType === "ul" ? "</ul>" : "</ol>");
  if (inTable) out.push("</tbody></table>");

  return out.join("\n");

  function closeList() {
    if (inList) {
      out.push(listType === "ul" ? "</ul>" : "</ol>");
      inList = false;
    }
  }
}

/** Inline markdown: bold, italic, links, code */
function inline(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/`(.+?)`/g, "<code>$1</code>");
}
